import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

/**
 * Unread-message counts for the current professional or appraiser.
 *
 * Tracks two granularities of unread state:
 *   - `byJob[jobId]`        — total unread on that job (used for sidebar + table)
 *   - `bySender[jobId][sid]` — unread per individual sender (used for the
 *                              professional's per-conversation indicator)
 *
 * Robustness:
 *   1. `total` is DERIVED via useMemo from `byJob` (single source of truth).
 *   2. `markConversationRead(jobId, senderId)` and `markJobRead(jobId)` both
 *      perform optimistic local updates AND register the cleared key in a
 *      short-lived "ignore window" so a slow or stale poll cannot resurrect
 *      counts the user has just read.
 *   3. Polling continues every 30 s and on window focus.
 *   4. State is NOT wiped on transient `user`-null transitions — only on a
 *      true logout / role change.
 */
const MessagesContext = createContext({
  total: 0,
  byJob: {},
  bySender: {},
  refresh: () => {},
  markJobRead: () => {},
  markConversationRead: () => {},
});

const POLL_INTERVAL_MS = 30 * 1000;
const IGNORE_WINDOW_MS = 60 * 1000;

export function MessagesProvider({ children }) {
  const { user } = useAuth();
  const [byJob, setByJob] = useState({});
  const [bySender, setBySender] = useState({}); // { [jobId]: { [senderId]: count } }

  // Map<key, expiresAt> — `key` is either `${jobId}` (whole job) or
  // `${jobId}|${senderId}` (single conversation). Polled responses for these
  // keys are forced to 0 until expiresAt, so a stale poll cannot un-clear
  // an optimistic mark-read.
  const ignoreUntilRef = useRef(new Map());
  const cancelledRef = useRef(false);

  const total = useMemo(
    () => Object.values(byJob).reduce((sum, n) => sum + (Number(n) || 0), 0),
    [byJob]
  );

  const isTrackedRole = !!user && (user.role === 'professional' || user.role === 'appraiser');

  const refresh = useCallback(async () => {
    if (!isTrackedRole) return;
    try {
      const res = await api.get('/api/messages/unread-counts');
      if (cancelledRef.current) return;
      const incomingByJob = { ...(res.data?.by_job || {}) };
      const incomingBySender = JSON.parse(JSON.stringify(res.data?.by_sender || {}));

      const now = Date.now();
      // Apply ignore-window suppressions
      for (const [key, exp] of ignoreUntilRef.current.entries()) {
        if (exp <= now) {
          ignoreUntilRef.current.delete(key);
          continue;
        }
        if (key.includes('|')) {
          const [jid, sid] = key.split('|');
          if (incomingBySender[jid] && sid in incomingBySender[jid]) {
            const dropped = incomingBySender[jid][sid] || 0;
            delete incomingBySender[jid][sid];
            if (Object.keys(incomingBySender[jid]).length === 0) delete incomingBySender[jid];
            if (incomingByJob[jid]) {
              incomingByJob[jid] = Math.max(0, incomingByJob[jid] - dropped);
              if (incomingByJob[jid] === 0) delete incomingByJob[jid];
            }
          }
        } else {
          delete incomingByJob[key];
          delete incomingBySender[key];
        }
      }
      setByJob(incomingByJob);
      setBySender(incomingBySender);
    } catch {
      // silent — keep last-known state
    }
  }, [isTrackedRole]);

  const markJobRead = useCallback(async (jobId) => {
    if (!jobId) return;
    ignoreUntilRef.current.set(jobId, Date.now() + IGNORE_WINDOW_MS);
    setByJob(prev => {
      if (!(jobId in prev)) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    setBySender(prev => {
      if (!(jobId in prev)) return prev;
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    try {
      await api.post(`/api/jobs/${jobId}/messages/mark-read`);
    } catch {
      ignoreUntilRef.current.delete(jobId);
    }
  }, []);

  const markConversationRead = useCallback(async (jobId, senderId) => {
    if (!jobId || !senderId) return;
    const key = `${jobId}|${senderId}`;
    ignoreUntilRef.current.set(key, Date.now() + IGNORE_WINDOW_MS);
    // Optimistic local update: drop senderId from this job, decrement byJob count
    setBySender(prev => {
      const jobMap = prev[jobId];
      if (!jobMap || !(senderId in jobMap)) return prev;
      const removed = jobMap[senderId] || 0;
      const newJobMap = { ...jobMap };
      delete newJobMap[senderId];
      const next = { ...prev };
      if (Object.keys(newJobMap).length === 0) delete next[jobId];
      else next[jobId] = newJobMap;
      // Mirror the decrement in byJob
      setByJob(jobs => {
        const cur = jobs[jobId] || 0;
        const nextJobs = { ...jobs };
        const remaining = Math.max(0, cur - removed);
        if (remaining === 0) delete nextJobs[jobId];
        else nextJobs[jobId] = remaining;
        return nextJobs;
      });
      return next;
    });
    try {
      await api.post(`/api/jobs/${jobId}/messages/mark-read?sender_id=${encodeURIComponent(senderId)}`);
    } catch {
      ignoreUntilRef.current.delete(key);
    }
  }, []);

  // Reset on logout / role change
  useEffect(() => {
    if (!isTrackedRole) {
      setByJob({});
      setBySender({});
      ignoreUntilRef.current.clear();
    }
  }, [isTrackedRole]);

  // Polling + focus refresh. Background tabs do not poll; they catch up as soon as they become visible again.
  useEffect(() => {
    cancelledRef.current = false;
    if (!isTrackedRole) return undefined;
    refresh();
    const id = setInterval(() => {
      if (!document.hidden) refresh();
    }, POLL_INTERVAL_MS);
    const onFocus = () => refresh();
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelledRef.current = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh, isTrackedRole]);

  return (
    <MessagesContext.Provider value={{ total, byJob, bySender, refresh, markJobRead, markConversationRead }}>
      {children}
    </MessagesContext.Provider>
  );
}

export const useMessages = () => useContext(MessagesContext);
